import { Receipt, User, XCircle } from 'lucide-react';
import { useState } from 'react';
import { METHODE_PAIEMENT_LABELS, type MethodePaiement } from '@sirajoptic/shared';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { ConfirmDialog, Drawer } from '@/components/ui/dialog';
import { InputField } from '@/components/ui/field';
import { ChargementBloc, Encart, EtatErreur } from '@/components/ui/feedback';
import { TBody, TD, TH, THead, TR, Table } from '@/components/ui/table';
import { useToast } from '@/components/ui/toast';
import { date, dateHeure, dh, entier } from '@/lib/format';
import { useAnnulerVente, useVente } from '../api/ventes.api';

/**
 * Détail d'une vente au comptoir.
 *
 * Une vente ne se modifie pas : elle est réglée et terminée au moment où elle
 * est enregistrée. La seule action possible est l'annulation, qui remet les
 * articles en stock sans effacer la sortie initiale du journal.
 */
export function VenteDrawer({ id, onFermer }: { id: string | null; onFermer: () => void }) {
  const { data, isPending, error, refetch } = useVente(id);
  const annuler = useAnnulerVente();
  const [aAnnuler, setAAnnuler] = useState(false);
  const [motif, setMotif] = useState('');
  const [erreurMotif, setErreurMotif] = useState<string | null>(null);
  const toast = useToast();

  if (!id) return null;

  const annulee = Boolean(data?.annulee_at);

  async function confirmerAnnulation() {
    if (!data) return;
    if (motif.trim().length < 3) {
      setErreurMotif("Indiquez le motif de l'annulation.");
      return;
    }
    try {
      await annuler.mutateAsync({ id: data.id, input: { motif: motif.trim() } });
      toast.succes(`Vente ${data.numero} annulée. Les articles sont revenus en stock.`);
      setAAnnuler(false);
      setMotif('');
      setErreurMotif(null);
    } catch (e) {
      toast.erreur(e instanceof Error ? e.message : "L'annulation a échoué.");
    }
  }

  return (
    <>
      <Drawer
        ouvert
        onFermer={onFermer}
        titre={data ? data.numero : 'Vente'}
        description={
          data
            ? `${date(data.date_vente)} · ${data.client_nom ?? 'Client de passage'}`
            : undefined
        }
        pied={
          data && !annulee ? (
            <Button
              variant="danger"
              icone={<XCircle aria-hidden className="h-4 w-4" />}
              onClick={() => setAAnnuler(true)}
            >
              Annuler la vente
            </Button>
          ) : null
        }
      >
        {isPending ? (
          <ChargementBloc libelle="Chargement de la vente…" />
        ) : error ? (
          <EtatErreur erreur={error} onReessayer={() => void refetch()} />
        ) : !data ? null : (
          <div className="space-y-4">
            {annulee ? (
              <Encart ton="danger" titre="Vente annulée">
                {data.motif_annulation}
                {data.annulee_at ? ` — ${dateHeure(data.annulee_at)}` : ''}
                <span className="mt-1 block">
                  Les articles ont été remis en stock ; la sortie d’origine reste au journal.
                </span>
              </Encart>
            ) : null}

            <div className="grid grid-cols-2 gap-3 text-sm">
              <div>
                <p className="text-xs uppercase tracking-wide text-ink-muted">Règlement</p>
                <p className="mt-0.5 text-ink">
                  {data.methode
                    ? METHODE_PAIEMENT_LABELS[data.methode as MethodePaiement]
                    : 'Non précisé'}
                </p>
              </div>
              <div>
                <p className="text-xs uppercase tracking-wide text-ink-muted">Encaissé</p>
                <p className="mt-0.5 font-semibold tabular text-ink">{dh(data.total)}</p>
              </div>
              <div>
                <p className="text-xs uppercase tracking-wide text-ink-muted">Vendu par</p>
                <p className="mt-0.5 flex items-center gap-1.5 text-ink">
                  <User aria-hidden className="h-3.5 w-3.5 text-ink-muted" />
                  {data.vendeur ?? '—'}
                </p>
              </div>
              <div>
                <p className="text-xs uppercase tracking-wide text-ink-muted">Enregistrée</p>
                <p className="mt-0.5 text-ink-muted">{dateHeure(data.created_at)}</p>
              </div>
            </div>

            <div className="rounded-md border border-line">
              <p className="flex items-center gap-2 border-b border-line bg-surface-sunken px-3 py-2 text-xs font-semibold uppercase tracking-wide text-ink-muted">
                <Receipt aria-hidden className="h-3.5 w-3.5" />
                Articles
              </p>
              <Table>
                <THead>
                  <TR>
                    <TH>Désignation</TH>
                    <TH numerique>Qté</TH>
                    <TH numerique>P.U.</TH>
                    <TH numerique>Total</TH>
                  </TR>
                </THead>
                <TBody>
                  {data.lignes.map((l) => (
                    <TR key={l.id}>
                      <TD>
                        {l.designation}
                        {/* Une ligne libre n'a jamais touché le stock : le dire
                            évite de chercher un mouvement qui n'existe pas. */}
                        {l.product_id ? null : (
                          <Badge ton="neutre" className="ml-2">
                            Hors stock
                          </Badge>
                        )}
                      </TD>
                      <TD numerique>{entier(l.quantite)}</TD>
                      <TD numerique className="text-ink-muted">
                        {dh(l.prix_unitaire)}
                      </TD>
                      <TD numerique className="font-medium">
                        {dh(l.total_ligne)}
                      </TD>
                    </TR>
                  ))}
                </TBody>
              </Table>
              <div className="flex items-center justify-between border-t border-line px-3 py-2">
                <span className="text-sm font-medium text-ink">Total</span>
                <span className="text-lg font-semibold tabular text-mustard-700">
                  {dh(data.total)}
                </span>
              </div>
            </div>

            {data.note ? (
              <div>
                <p className="text-xs uppercase tracking-wide text-ink-muted">Note</p>
                <p className="mt-0.5 text-sm text-ink-soft">{data.note}</p>
              </div>
            ) : null}
          </div>
        )}
      </Drawer>

      <ConfirmDialog
        ouvert={aAnnuler}
        onFermer={() => setAAnnuler(false)}
        onConfirmer={() => void confirmerAnnulation()}
        titre="Annuler cette vente ?"
        libelleConfirmer="Annuler la vente"
        danger
        chargement={annuler.isPending}
        message="Les articles reviendront en stock et le montant sortira du chiffre d’affaires. La vente reste consultable, marquée comme annulée."
      >
        <InputField
          label="Motif"
          value={motif}
          onChange={(e) => {
            setMotif(e.target.value);
            setErreurMotif(null);
          }}
          erreur={erreurMotif ?? undefined}
          requis
        />
      </ConfirmDialog>
    </>
  );
}
